// src/components/SetupDetail.js
import React, { useState, useEffect, useMemo, useContext } from 'react';
import { TeamPlanContext } from '../App';
import TireLifingForm from './tires/TireLifingForm';
import { getTire } from '../utils/tiresService';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import { db } from '../firebaseConfig';
import {
  doc,
  getDoc,
  deleteDoc,
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  serverTimestamp,
  onSnapshot,
  orderBy
} from 'firebase/firestore';
import { useTranslation } from 'react-i18next';

// --- IMPORT MUI ---
import {
  Box,
  Typography, CircularProgress, Button,
  IconButton, Paper, Grid,
  List, ListItem, ListItemIcon, ListItemText, Checkbox,
  Dialog, DialogTitle, DialogContent, DialogActions,
  RadioGroup, FormControlLabel, Radio, Alert,
  Tooltip,
  Link,
  Divider,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import LinkIcon from '@mui/icons-material/Link';
import SaveIcon from '@mui/icons-material/Save';
import NotesIcon from '@mui/icons-material/Notes';
import TimerIcon from '@mui/icons-material/Timer';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'; // Import Icona Export PDF
import TimelineIcon from '@mui/icons-material/Timeline';
import CampaignIcon from '@mui/icons-material/Campaign';
import MicIcon from '@mui/icons-material/Mic';
import { exportSetupToExcel } from '../utils/ExcelExport'; // Import Utility Export

import SetupPrintLayout from './SetupPrintLayout'; // Manteniamo la stampa
import SetupPdf from './SetupPdf';
import { pdf } from '@react-pdf/renderer';
import CarCentricView from './setup_car_centric/CarCentricView';

// --- 1. DEFINIZIONE DELLO STATO INIZIALE COMPLETO ---
const newInitialSetupState = {
  meteo: {}, feedback: {}, anteriore: { ammortizzatori: {} },
  posteriore: { ammortizzatori: {} }, gomme_ant_sx: {}, gomme_ant_dx: {},
  gomme_post_sx: {}, gomme_post_dx: {}, aero: {}, differenziale: {},
  weights: {}, elettronica: {}, freni: {}, strategia: {}
};

// --- 2. COMPONENTI HELPER DI VISUALIZZAZIONE ---
const DataDisplay = ({ label, value, unit = '', xs = 6, sm = 6, md = 6, alwaysShow = false }) => {
  const isEmpty = value === null || value === undefined || value === '';
  if (!alwaysShow && isEmpty) return null;
  const displayValue = isEmpty ? '-' : `${value} ${unit}`.trim();
  return (
    <Grid item xs={xs} sm={sm} md={md}>
      <Typography variant="body2" color="textSecondary" noWrap>{label}</Typography>
      <Typography variant="h6" noWrap>{displayValue}</Typography>
    </Grid>
  );
};
const NoteDisplay = ({ label, value, xs = 12 }) => {
  if (!value) return null;
  return (
    <Grid item xs={xs}>
      <Typography variant="body2" color="textSecondary">{label}</Typography>
      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {value}
      </Typography>
    </Grid>
  );
};
const LinkDisplay = ({ label, link, description }) => {
  if (!link) return null;
  return (
    <Grid item xs={12} sm={6}>
      <Typography variant="body2" color="textSecondary">{label}</Typography>
      <Link href={link} target="_blank" rel="noopener noreferrer" sx={{ display: 'flex', alignItems: 'center', wordBreak: 'break-all' }}>
        <LinkIcon fontSize="small" sx={{ mr: 0.5 }} />
        {description || link}
      </Link>
    </Grid>
  );
};
const subHeaderStyle = { width: '100%', mt: 2, mb: 1, fontWeight: 600, color: 'text.secondary', borderBottom: 1, borderColor: 'divider', pb: 1 };
const accordionStyle = { // mantenuto per retrocompatibilità con eventuali formati template vecchi
  elevation: 0,
  disableGutters: true,
  sx: {
    elevation: 0,
    border: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'background.default',
    '&:before': { display: 'none' },
    mb: 1
  }
};
// --- FINE HELPER ---


// --- 3. MODULI "LEGO" DI SOLA VISUALIZZAZIONE ---

const QuadrantBox = ({ title, children }) => (
  <Grid item xs={12} md={6} lg={6}>
    <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, height: '100%' }}>
      <Typography variant="subtitle2" gutterBottom>{title}</Typography>
      <Divider sx={{ mb: 1 }} />
      <Grid container spacing={2}>
        {children}
      </Grid>
    </Box>
  </Grid>
);

const ModuleDisplayWeights = ({ data, t, totalCalculatedWeight, calculatedBallastValue, customFields = [] }) => (
  <Box>
    <Typography sx={subHeaderStyle}>{t('subheader_weights_base', { ns: 'setup' })}</Typography>
    <Grid container spacing={2}>
      <DataDisplay label={t('detail_weights_min_reg')} value={data.minRegWeight} unit="kg" />
      <DataDisplay label={t('detail_weights_driver')} value={data.driverWeight} unit="kg" />
      <DataDisplay label={t('detail_weights_dry')} value={data.dryWeight} unit="kg" />
      <DataDisplay label={t('detail_weights_fuel')} value={data.fuelLiters} unit="L" />
    </Grid>

    <Typography sx={subHeaderStyle} mt={3}>{t('subheader_weights_total', { ns: 'setup' })}</Typography>
    <Grid container spacing={2}>
      <DataDisplay label={t('detail_weights_total_measured')} value={data.totalWeight} unit="kg" />
      <DataDisplay label={t('detail_weights_total_calc')} value={totalCalculatedWeight.toFixed(1)} unit="kg" />
      <DataDisplay label={t('detail_weights_front_axle')} value={data.totalFrontWeight} unit="kg" />
      <DataDisplay label={t('detail_weights_rear_axle')} value={data.totalRearWeight} unit="kg" />
      <DataDisplay label={t('detail_weights_dist')} value={data.weightRip} unit="%" />
    </Grid>

    <Typography sx={subHeaderStyle} mt={3}>{t('subheader_weights_corner', { ns: 'setup' })}</Typography>
    <Grid container spacing={2}>
      <QuadrantBox title={t('quadrant_fl')}>
        <DataDisplay label={t('field_corner_fl', { ns: 'setup' })} value={data.cornerFL} unit="kg" xs={12} />
      </QuadrantBox>
      <QuadrantBox title={t('quadrant_fr')}>
        <DataDisplay label={t('field_corner_fr', { ns: 'setup' })} value={data.cornerFR} unit="kg" xs={12} />
      </QuadrantBox>
      <QuadrantBox title={t('quadrant_rl')}>
        <DataDisplay label={t('field_corner_rl', { ns: 'setup' })} value={data.cornerRL} unit="kg" xs={12} />
      </QuadrantBox>
      <QuadrantBox title={t('quadrant_rr')}>
        <DataDisplay label={t('field_corner_rr', { ns: 'setup' })} value={data.cornerRR} unit="kg" xs={12} />
      </QuadrantBox>
    </Grid>

    <Typography sx={subHeaderStyle} mt={3}>{t('subheader_weights_ballast', { ns: 'setup' })}</Typography>
    <Grid container spacing={2}>
      <DataDisplay label={t('detail_weights_req_ballast')} value={calculatedBallastValue} unit="kg" />
      <DataDisplay label={t('detail_weights_applied_ballast')} value={data.ballast} unit="kg" />
    </Grid>

    {customFields && customFields.length > 0 && (
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" gutterBottom>{t('subheader_custom_fields', { defaultValue: 'Campi Personalizzati' })}</Typography>
        <Grid container spacing={2}>
          {customFields.map((cf, idx) => <DataDisplay alwaysShow key={idx} label={cf.label} value={data?.[cf.key]} xs={12} sm={6} md={4} />)}
        </Grid>
      </Box>
    )}
  </Box>
);

const ModuleDisplayAlignment = ({ dataAnteriore, dataPosteriore, t, customFields = [] }) => {
  const getCustomFields = (pos) => customFields.filter(f => (f.position || 'General') === pos);
  return (
  <Box>
    <Typography variant="h6" gutterBottom>{t('detail_front_title')}</Typography>
    <Grid container spacing={2} sx={{ mb: 3 }}>
      <QuadrantBox title={t('quadrant_fl')}>
        <DataDisplay label={t('field_toe', { ns: 'setup' })} value={dataAnteriore?.convergenzaSX} unit={`${dataAnteriore?.toeInOrOutSX || 'out'} (mm)`} xs={12} sm={6} />
        <DataDisplay label={t('field_camber', { ns: 'setup' })} value={dataAnteriore?.camberSX} unit="deg" xs={12} sm={6} />
        <DataDisplay label={t('field_caster', { ns: 'setup' })} value={dataAnteriore?.casterSX} unit="deg" xs={12} sm={6} />
        {getCustomFields('FL').map((cf, idx) => <DataDisplay alwaysShow key={idx} label={cf.label} value={dataAnteriore?.[cf.key]} xs={12} sm={6} />)}
      </QuadrantBox>
      <QuadrantBox title={t('quadrant_fr')}>
        <DataDisplay label={t('field_toe', { ns: 'setup' })} value={dataAnteriore?.convergenzaDX} unit={`${dataAnteriore?.toeInOrOutDX || 'out'} (mm)`} xs={12} sm={6} />
        <DataDisplay label={t('field_camber', { ns: 'setup' })} value={dataAnteriore?.camberDX} unit="deg" xs={12} sm={6} />
        <DataDisplay label={t('field_caster', { ns: 'setup' })} value={dataAnteriore?.casterDX} unit="deg" xs={12} sm={6} />
        {getCustomFields('FR').map((cf, idx) => <DataDisplay alwaysShow key={idx} label={cf.label} value={dataAnteriore?.[cf.key]} xs={12} sm={6} />)}
      </QuadrantBox>
    </Grid>
    
    <Typography variant="h6" gutterBottom>{t('detail_rear_title')}</Typography>
    <Grid container spacing={2}>
      <QuadrantBox title={t('quadrant_rl')}>
        <DataDisplay label={t('field_toe', { ns: 'setup' })} value={dataPosteriore?.convergenzaSX} unit={`${dataPosteriore?.toeInOrOutSX || 'in'} (mm)`} xs={12} sm={6} />
        <DataDisplay label={t('field_camber', { ns: 'setup' })} value={dataPosteriore?.camberSX} unit="deg" xs={12} sm={6} />
        {getCustomFields('RL').map((cf, idx) => <DataDisplay alwaysShow key={idx} label={cf.label} value={dataPosteriore?.[cf.key]} xs={12} sm={6} />)}
      </QuadrantBox>
      <QuadrantBox title={t('quadrant_rr')}>
        <DataDisplay label={t('field_toe', { ns: 'setup' })} value={dataPosteriore?.convergenzaDX} unit={`${dataPosteriore?.toeInOrOutDX || 'in'} (mm)`} xs={12} sm={6} />
        <DataDisplay label={t('field_camber', { ns: 'setup' })} value={dataPosteriore?.camberDX} unit="deg" xs={12} sm={6} />
        {getCustomFields('RR').map((cf, idx) => <DataDisplay alwaysShow key={idx} label={cf.label} value={dataPosteriore?.[cf.key]} xs={12} sm={6} />)}
      </QuadrantBox>
    </Grid>

    {getCustomFields('General').length > 0 && (
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" gutterBottom>{t('subheader_custom_fields', { defaultValue: 'Campi Personalizzati' })} (Generali)</Typography>
        <Grid container spacing={2}>
          {getCustomFields('General').map((cf, idx) => <DataDisplay alwaysShow key={idx} label={cf.label} value={dataAnteriore?.[cf.key] || dataPosteriore?.[cf.key]} xs={12} sm={6} md={4} />)}
        </Grid>
      </Box>
    )}
  </Box>
  );
};

const ModuleDisplayMechanicalGrip = ({ dataAnteriore, dataPosteriore, dataDifferenziale, dataFreni, t, customFields = [] }) => {
  const getCustomFields = (pos) => customFields.filter(f => (f.position || 'General') === pos);
  return (
  <Box>
    <Typography sx={subHeaderStyle}>{t('subheader_springs', { ns: 'setup' })}</Typography>
    <Grid container spacing={2}>
      <QuadrantBox title={t('quadrant_fl')}>
        <DataDisplay label={t('field_spring_rate', { ns: 'setup' })} value={dataAnteriore?.mollaSX} xs={12} sm={6} />
        <DataDisplay label={t('field_bumpstop_rate', { ns: 'setup' })} value={dataAnteriore?.rigidezzaBumpstopSX} xs={12} sm={6} />
        <DataDisplay label={t('field_bumpstop_range', { ns: 'setup' })} value={dataAnteriore?.distanzaBumpstopSX} unit="mm" xs={12} sm={6} />
        {getCustomFields('FL').map((cf, idx) => <DataDisplay alwaysShow key={idx} label={cf.label} value={dataAnteriore?.[cf.key]} xs={12} sm={6} />)}
      </QuadrantBox>
      <QuadrantBox title={t('quadrant_fr')}>
        <DataDisplay label={t('field_spring_rate', { ns: 'setup' })} value={dataAnteriore?.mollaDX} xs={12} sm={6} />
        <DataDisplay label={t('field_bumpstop_rate', { ns: 'setup' })} value={dataAnteriore?.rigidezzaBumpstopDX} xs={12} sm={6} />
        <DataDisplay label={t('field_bumpstop_range', { ns: 'setup' })} value={dataAnteriore?.distanzaBumpstopDX} unit="mm" xs={12} sm={6} />
        {getCustomFields('FR').map((cf, idx) => <DataDisplay alwaysShow key={idx} label={cf.label} value={dataAnteriore?.[cf.key]} xs={12} sm={6} />)}
      </QuadrantBox>
      <QuadrantBox title={t('quadrant_rl')}>
        <DataDisplay label={t('field_spring_rate', { ns: 'setup' })} value={dataPosteriore?.mollaSX} xs={12} sm={6} />
        <DataDisplay label={t('field_bumpstop_rate', { ns: 'setup' })} value={dataPosteriore?.rigidezzaBumpstopSX} xs={12} sm={6} />
        <DataDisplay label={t('field_bumpstop_range', { ns: 'setup' })} value={dataPosteriore?.distanzaBumpstopSX} unit="mm" xs={12} sm={6} />
        {getCustomFields('RL').map((cf, idx) => <DataDisplay alwaysShow key={idx} label={cf.label} value={dataPosteriore?.[cf.key]} xs={12} sm={6} />)}
      </QuadrantBox>
      <QuadrantBox title={t('quadrant_rr')}>
        <DataDisplay label={t('field_spring_rate', { ns: 'setup' })} value={dataPosteriore?.mollaDX} xs={12} sm={6} />
        <DataDisplay label={t('field_bumpstop_rate', { ns: 'setup' })} value={dataPosteriore?.rigidezzaBumpstopDX} xs={12} sm={6} />
        <DataDisplay label={t('field_bumpstop_range', { ns: 'setup' })} value={dataPosteriore?.distanzaBumpstopDX} unit="mm" xs={12} sm={6} />
        {getCustomFields('RR').map((cf, idx) => <DataDisplay alwaysShow key={idx} label={cf.label} value={dataPosteriore?.[cf.key]} xs={12} sm={6} />)}
      </QuadrantBox>
    </Grid>

    <Typography sx={subHeaderStyle} mt={3}>{t('subheader_chassis', { ns: 'setup' })}</Typography>
    <Grid container spacing={2}>
      <DataDisplay label={t('field_arb_front')} value={dataAnteriore?.barra} />
      <DataDisplay label={t('field_arb_rear')} value={dataPosteriore?.barra} />
      <DataDisplay label={t('field_brake_power', { ns: 'setup' })} value={dataFreni?.potenzaFreni} unit="%" />
      <DataDisplay label={t('field_brake_bias', { ns: 'setup' })} value={dataFreni?.bilanciamentoFreni} unit="%" />
      <DataDisplay label={t('field_steering_ratio', { ns: 'setup' })} value={dataFreni?.rapportoSterzo} />
      <DataDisplay label={t('field_diff_preload')} value={dataDifferenziale?.precarico} unit="Nm" />
      <DataDisplay label={t('field_diff_lock')} value={dataDifferenziale?.bloccaggio} unit="%" />
    </Grid>

    {getCustomFields('General').length > 0 && (
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" gutterBottom>{t('subheader_custom_fields', { defaultValue: 'Campi Personalizzati' })} (Generali / Telaio)</Typography>
        <Grid container spacing={2}>
          {getCustomFields('General').map((cf, idx) => <DataDisplay alwaysShow key={idx} label={cf.label} value={dataAnteriore?.[cf.key] || dataPosteriore?.[cf.key] || dataFreni?.[cf.key] || dataDifferenziale?.[cf.key]} xs={12} sm={6} md={4} />)}
        </Grid>
      </Box>
    )}
  </Box>
  );
};

const ModuleDisplayDampers = ({ dataAnteriore, dataPosteriore, t, customFields = [] }) => {
  const getCustomFields = (pos) => customFields.filter(f => (f.position || 'General') === pos);
  return (
  <Box>
  <Grid container spacing={2}>
      <QuadrantBox title={t('quadrant_fl')}>
        <DataDisplay label={t('field_damp_bump', { ns: 'setup' })} value={dataAnteriore?.ammortizzatori?.bumpSX} xs={6} />
        <DataDisplay label={t('field_damp_rebound', { ns: 'setup' })} value={dataAnteriore?.ammortizzatori?.reboundSX} xs={6} />
        <DataDisplay label={t('field_damp_fast_bump', { ns: 'setup' })} value={dataAnteriore?.ammortizzatori?.fastBumpSX} xs={6} />
        <DataDisplay label={t('field_damp_fast_rebound', { ns: 'setup' })} value={dataAnteriore?.ammortizzatori?.fastReboundSX} xs={6} />
        {getCustomFields('FL').map((cf, idx) => <DataDisplay alwaysShow key={idx} label={cf.label} value={dataAnteriore?.ammortizzatori?.[cf.key]} xs={6} />)}
      </QuadrantBox>
      <QuadrantBox title={t('quadrant_fr')}>
        <DataDisplay label={t('field_damp_bump', { ns: 'setup' })} value={dataAnteriore?.ammortizzatori?.bumpDX} xs={6} />
        <DataDisplay label={t('field_damp_rebound', { ns: 'setup' })} value={dataAnteriore?.ammortizzatori?.reboundDX} xs={6} />
        <DataDisplay label={t('field_damp_fast_bump', { ns: 'setup' })} value={dataAnteriore?.ammortizzatori?.fastBumpDX} xs={6} />
        <DataDisplay label={t('field_damp_fast_rebound', { ns: 'setup' })} value={dataAnteriore?.ammortizzatori?.fastReboundDX} xs={6} />
        {getCustomFields('FR').map((cf, idx) => <DataDisplay alwaysShow key={idx} label={cf.label} value={dataAnteriore?.ammortizzatori?.[cf.key]} xs={6} />)}
      </QuadrantBox>
      <QuadrantBox title={t('quadrant_rl')}>
        <DataDisplay label={t('field_damp_bump', { ns: 'setup' })} value={dataPosteriore?.ammortizzatori?.bumpSX} xs={6} />
        <DataDisplay label={t('field_damp_rebound', { ns: 'setup' })} value={dataPosteriore?.ammortizzatori?.reboundSX} xs={6} />
        <DataDisplay label={t('field_damp_fast_bump', { ns: 'setup' })} value={dataPosteriore?.ammortizzatori?.fastBumpSX} xs={6} />
        <DataDisplay label={t('field_damp_fast_rebound', { ns: 'setup' })} value={dataPosteriore?.ammortizzatori?.fastReboundSX} xs={6} />
        {getCustomFields('RL').map((cf, idx) => <DataDisplay alwaysShow key={idx} label={cf.label} value={dataPosteriore?.ammortizzatori?.[cf.key]} xs={6} />)}
      </QuadrantBox>
      <QuadrantBox title={t('quadrant_rr')}>
        <DataDisplay label={t('field_damp_bump', { ns: 'setup' })} value={dataPosteriore?.ammortizzatori?.bumpDX} xs={6} />
        <DataDisplay label={t('field_damp_rebound', { ns: 'setup' })} value={dataPosteriore?.ammortizzatori?.reboundDX} xs={6} />
        <DataDisplay label={t('field_damp_fast_bump', { ns: 'setup' })} value={dataPosteriore?.ammortizzatori?.fastBumpDX} xs={6} />
        <DataDisplay label={t('field_damp_fast_rebound', { ns: 'setup' })} value={dataPosteriore?.ammortizzatori?.fastReboundDX} xs={6} />
        {getCustomFields('RR').map((cf, idx) => <DataDisplay alwaysShow key={idx} label={cf.label} value={dataPosteriore?.ammortizzatori?.[cf.key]} xs={6} />)}
      </QuadrantBox>
    </Grid>

    {getCustomFields('General').length > 0 && (
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" gutterBottom>{t('subheader_custom_fields', { defaultValue: 'Campi Personalizzati' })} (Generali)</Typography>
        <Grid container spacing={2}>
          {getCustomFields('General').map((cf, idx) => <DataDisplay alwaysShow key={idx} label={cf.label} value={dataAnteriore?.ammortizzatori?.[cf.key] || dataPosteriore?.ammortizzatori?.[cf.key]} xs={12} sm={6} md={4} />)}
        </Grid>
      </Box>
    )}
  </Box>
  );
};

const ModuleDisplayAero = ({ dataAnteriore, dataPosteriore, dataAero, t, customFields = [] }) => {
  return (
  <Box>
    <Typography sx={subHeaderStyle}>{t('subheader_ride_heights')}</Typography>
    <Grid container spacing={2}>
      <DataDisplay label={t('field_ride_height_front')} value={dataAnteriore?.altezzaTargetSX} unit="mm" />
      <DataDisplay label={t('field_ride_height_rear')} value={dataPosteriore?.altezzaTargetSX} unit="mm" />
    </Grid>
    <Typography sx={subHeaderStyle}>{t('subheader_aero_components')}</Typography>
    <Grid container spacing={2}>
      <DataDisplay label={t('field_wing_rear')} value={dataAero?.alaPosteriore} />
      <DataDisplay label={t('field_splitter')} value={dataAero?.splitterAnteriore} />
    </Grid>
    <Typography sx={subHeaderStyle}>{t('subheader_brake_ducts')}</Typography>
    <Grid container spacing={2}>
      <DataDisplay label={t('field_brake_ducts_front')} value={dataAero?.condottiFrenoAnteriori} />
      <DataDisplay label={t('field_brake_ducts_rear')} value={dataAero?.condottiFrenoPosteriori} />
    </Grid>

    {customFields && customFields.length > 0 && (
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" gutterBottom>{t('subheader_custom_fields', { defaultValue: 'Campi Personalizzati' })}</Typography>
        <Grid container spacing={2}>
          {customFields.map((cf, idx) => <DataDisplay alwaysShow key={idx} label={cf.label} value={dataAero?.[cf.key] || dataAnteriore?.[cf.key] || dataPosteriore?.[cf.key]} xs={12} sm={6} md={4} />)}
        </Grid>
      </Box>
    )}
  </Box>
  );
};

const ModuleDisplayElectronics = ({ data, t, customFields = [] }) => (
  <Box>
    <Grid container spacing={2}>
      <DataDisplay label={t('field_electronics_tc1')} value={data?.tc1} />
      <DataDisplay label={t('field_electronics_tc2')} value={data?.tc2} />
      <DataDisplay label={t('field_electronics_abs')} value={data?.abs} />
      <DataDisplay label={t('field_electronics_ecu')} value={data?.mappaECU} />
    </Grid>
    {customFields && customFields.length > 0 && (
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" gutterBottom>{t('subheader_custom_fields', { defaultValue: 'Campi Personalizzati' })}</Typography>
        <Grid container spacing={2}>
          {customFields.map((cf, idx) => <DataDisplay alwaysShow key={idx} label={cf.label} value={data?.[cf.key]} xs={12} sm={6} md={4} />)}
        </Grid>
      </Box>
    )}
  </Box>
);

const TireDisplaySet = ({ label, tireData, t, customFields = [] }) => {
  const pFreddo = parseFloat(tireData?.pFreddo) || 0;
  const pMeasured = parseFloat(tireData?.pCaldoMeasured) || 0;
  const pTarget = parseFloat(tireData?.pCaldoTarget) || 0;
  const deltaP = pMeasured > 0 && pFreddo > 0 ? (pMeasured - pFreddo).toFixed(2) : 'N/A';
  const deltaT = pMeasured > 0 && pTarget > 0 ? (pMeasured - pTarget).toFixed(2) : 'N/A';

  return (
    <QuadrantBox title={label}>
      <DataDisplay label={t('field_p_cold', { ns: 'setup' })} value={tireData?.pFreddo} unit="bar" xs={6} sm={6} />
      <DataDisplay label={t('field_p_hot_target', { ns: 'setup' })} value={tireData?.pCaldoTarget} unit="bar" xs={6} sm={6} />
      <DataDisplay label={t('detail_tires_phot_measured')} value={tireData?.pCaldoMeasured} unit="bar" xs={6} sm={6} />
      <DataDisplay label={t('detail_tires_phot_adjusted')} value={tireData?.pCaldoAdjusted} unit="bar" xs={6} sm={6} />
      <DataDisplay label={t('detail_tires_delta_p')} value={deltaP} unit="bar" xs={6} sm={6} />
      <DataDisplay label={t('detail_tires_delta_t')} value={deltaT} unit="bar" xs={6} sm={6} />
      {tireData?.tInt && <DataDisplay label={t('detail_tires_tint')} value={tireData?.tInt} unit="°C" xs={4} />}
      {tireData?.tMed && <DataDisplay label={t('detail_tires_tmid')} value={tireData?.tMed} unit="°C" xs={4} />}
      {tireData?.tExt && <DataDisplay label={t('detail_tires_text')} value={tireData?.tExt} unit="°C" xs={4} />}
      {customFields.map((cf, idx) => <DataDisplay alwaysShow key={idx} label={cf.label} value={tireData?.[cf.key]} xs={6} />)}
    </QuadrantBox>
  );
};

const ModuleDisplayStrategyTires = ({ dataGomme, dataFuel, dataFreni, t, customFields = [] }) => {
  const getCustomFields = (pos) => customFields.filter(f => (f.position || 'General') === pos);
  return (
  <Box>
    <Typography sx={subHeaderStyle}>{t('subheader_tires_base', { ns: 'setup' })}</Typography>
    <Grid container spacing={2}>
      <TireDisplaySet label={t('detail_tires_fl')} tireData={dataGomme?.gomme_ant_sx} t={t} customFields={getCustomFields('FL')} />
      <TireDisplaySet label={t('detail_tires_fr')} tireData={dataGomme?.gomme_ant_dx} t={t} customFields={getCustomFields('FR')} />
      <TireDisplaySet label={t('detail_tires_rl')} tireData={dataGomme?.gomme_post_sx} t={t} customFields={getCustomFields('RL')} />
      <TireDisplaySet label={t('detail_tires_rr')} tireData={dataGomme?.gomme_post_dx} t={t} customFields={getCustomFields('RR')} />
    </Grid>

    <Typography sx={subHeaderStyle}>{t('subheader_strategy_fuel')}</Typography>
    <Grid container spacing={2}>
      <DataDisplay label={t('field_strategy_fuel_start')} value={dataFuel?.carburanteIniziale} unit="L" />
      <DataDisplay label={t('field_strategy_fuel_consumption')} value={dataFuel?.fuelConsumption} unit="L/giro" />
    </Grid>

    <Typography sx={subHeaderStyle}>{t('subheader_strategy_brakes')}</Typography>
    <Grid container spacing={2}>
      <DataDisplay label={t('field_strategy_brakes_front')} value={dataFreni?.pastiglieFrenoAnteriori} />
      <DataDisplay label={t('field_strategy_brakes_rear')} value={dataFreni?.pastiglieFrenoPosteriori} />
    </Grid>

    <Typography sx={subHeaderStyle}>{t('subheader_strategy_pit')}</Typography>
    <Grid container spacing={2}>
      <NoteDisplay label={t('field_strategy_pit_plan')} value={dataFuel?.strategiaPit} xs={12} />
    </Grid>

    {getCustomFields('General').length > 0 && (
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" gutterBottom>{t('subheader_custom_fields', { defaultValue: 'Campi Personalizzati' })} (Generali / Strategia)</Typography>
        <Grid container spacing={2}>
          {getCustomFields('General').map((cf, idx) => <DataDisplay alwaysShow key={idx} label={cf.label} value={dataFuel?.[cf.key] || dataFreni?.[cf.key]} xs={12} sm={6} md={4} />)}
        </Grid>
      </Box>
    )}
  </Box>
  );
};

const ModuleDisplayFeedback = ({ data, t, customFields = [] }) => (
  <Box>
    <Grid container spacing={2}>
      <DataDisplay label={t('detail_feedback_best_lap')} value={data?.bestLap} />
      <NoteDisplay label={t('detail_feedback_times')} value={data?.listaTempi} xs={12} />
      <NoteDisplay label={t('detail_feedback_tech_notes')} value={data?.noteTecnico} xs={12} />
      <NoteDisplay label={t('detail_feedback_driver')} value={data?.feedbackPilota} xs={12} />
      <LinkDisplay label={t('detail_feedback_telemetry')} link={data?.linkTelemetria} description={data?.descrizioneTelemetria} />
      <LinkDisplay label={t('detail_feedback_video')} link={data?.linkVideo} description={data?.descrizioneVideo} />
    </Grid>
    {customFields && customFields.length > 0 && (
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" gutterBottom>{t('subheader_custom_fields', { defaultValue: 'Campi Personalizzati' })}</Typography>
        <Grid container spacing={2}>
          {customFields.map((cf, idx) => <DataDisplay alwaysShow key={idx} label={cf.label} value={data?.[cf.key]} xs={12} sm={6} md={4} />)}
        </Grid>
      </Box>
    )}
  </Box>
);
// --- FINE MODULI "LEGO" ---


// --- 4. CORREZIONE: Sposta TireInputSet PRIMA di PitLaneMonitor ---
const TireInputSet = ({ label, tireData, onChange, t }) => {
  const pFreddo = parseFloat(tireData.pFreddo) || 0;
  const pMeasured = parseFloat(tireData.pCaldoMeasured) || 0;
  const pTarget = parseFloat(tireData.pCaldoTarget) || 0;

  const deltaP = pMeasured > 0 && pFreddo > 0 ? (pMeasured - pFreddo).toFixed(2) : 'N/A';
  const deltaT = pMeasured > 0 && pTarget > 0 ? (pMeasured - pTarget).toFixed(2) : 'N/A';

  return (
    <Grid item xs={12} md={6} lg={6}>
      <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', height: '100%' }}>
        <Typography variant="h6" gutterBottom>{label}</Typography>
        <Typography variant="body2" color="textSecondary">{t('pit_monitor_target')} {tireData.pCaldoTarget || 'N/A'} bar</Typography>
        <Divider sx={{ my: 1 }} />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('pit_monitor_p_hot_measured')}
              type="number" inputProps={{ step: "0.01" }}
              value={tireData.pCaldoMeasured || ''}
              onChange={(e) => onChange('pCaldoMeasured', e.target.value)}
              fullWidth margin="dense" size="small"
              helperText={`ΔP: ${deltaP} | ΔT: ${deltaT}`}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('pit_monitor_p_hot_adjusted')}
              type="number" inputProps={{ step: "0.01" }}
              value={tireData.pCaldoAdjusted || ''}
              onChange={(e) => onChange('pCaldoAdjusted', e.target.value)}
              fullWidth margin="dense" size="small"
            />
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ my: 0.5 }} />
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <TextField
                  label={t('field_t_int', { ns: 'setup' })}
                  type="number"
                  inputProps={{ step: "1" }}
                  size="small"
                  value={tireData.tInt || ''}
                  onChange={(e) => onChange('tInt', e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={t('field_t_mid', { ns: 'setup' })}
                  type="number"
                  inputProps={{ step: "1" }}
                  size="small"
                  value={tireData.tMed || ''}
                  onChange={(e) => onChange('tMed', e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={t('field_t_ext', { ns: 'setup' })}
                  type="number"
                  inputProps={{ step: "1" }}
                  size="small"
                  value={tireData.tExt || ''}
                  onChange={(e) => onChange('tExt', e.target.value)}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>
    </Grid>
  );
};
// --- FINE SPOSTAMENTO ---


// --- 5. COMPONENTE PITLANE MONITOR ---
const PitLaneMonitor = ({ setupData, setupId, isManager, isMeccanico, t }) => {
  const canEdit = isManager || isMeccanico;
  const [isSaving, setIsSaving] = useState(false);

  const [tires, setTires] = useState({
    gomme_ant_sx: setupData.gomme_ant_sx || {},
    gomme_ant_dx: setupData.gomme_ant_dx || {},
    gomme_post_sx: setupData.gomme_post_sx || {},
    gomme_post_dx: setupData.gomme_post_dx || {}
  });

  const [lapTimes, setLapTimes] = useState(setupData.feedback?.listaTempi || '');
  const [lapNotes, setLapNotes] = useState(setupData.feedback?.noteTecnico || '');

  useEffect(() => {
    setTires({
      gomme_ant_sx: setupData.gomme_ant_sx || {},
      gomme_ant_dx: setupData.gomme_ant_dx || {},
      gomme_post_sx: setupData.gomme_post_sx || {},
      gomme_post_dx: setupData.gomme_post_dx || {}
    });
    setLapTimes(setupData.feedback?.listaTempi || '');
    setLapNotes(setupData.feedback?.noteTecnico || '');
  }, [setupData]);

  const handleChange = (tireSet, field, value) => {
    setTires(prev => ({
      ...prev,
      [tireSet]: {
        ...prev[tireSet],
        [field]: value
      }
    }));
  };

  const handleSaveTires = async () => {
    if (!canEdit) return;
    setIsSaving(true);
    try {
      const setupRef = doc(db, 'setupSheets', setupId);
      await updateDoc(setupRef, {
        'setupData.gomme_ant_sx': tires.gomme_ant_sx,
        'setupData.gomme_ant_dx': tires.gomme_ant_dx,
        'setupData.gomme_post_sx': tires.gomme_post_sx,
        'setupData.gomme_post_dx': tires.gomme_post_dx,
        'setupData.feedback.listaTempi': lapTimes,
        'setupData.feedback.noteTecnico': lapNotes,
      });
    } catch (error) {
      console.error("Error saving pit lane data:", error);
      alert(t('pit_monitor_save_error'));
    } finally {
      setIsSaving(false);
    }
  };

  if (!canEdit) return null;

  return (
    <Paper
      elevation={0}
      sx={{ p: { xs: 2, sm: 3 }, mb: 3, border: '1px solid', borderColor: 'divider', backgroundColor: 'background.paper' }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">{t('pit_monitor_title')}</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSaveTires}
          disabled={isSaving}
          startIcon={isSaving ? <CircularProgress size={20} /> : <SaveIcon />}
        >
          {t('pit_monitor_save')}
        </Button>
      </Box>
      <Grid container spacing={2}>
        {/* --- 6. ERRORE CORRETTO --- */}
        <TireInputSet label={t('quadrant_fl')} tireData={tires.gomme_ant_sx} onChange={(field, value) => handleChange('gomme_ant_sx', field, value)} t={t} />
        <TireInputSet label={t('quadrant_fr')} tireData={tires.gomme_ant_dx} onChange={(field, value) => handleChange('gomme_ant_dx', field, value)} t={t} />
        <TireInputSet label={t('quadrant_rl')} tireData={tires.gomme_post_sx} onChange={(field, value) => handleChange('gomme_post_sx', field, value)} t={t} />
        <TireInputSet label={t('quadrant_rr')} tireData={tires.gomme_post_dx} onChange={(field, value) => handleChange('gomme_post_dx', field, value)} t={t} />
        {/* --- FINE ERRORE --- */}
      </Grid>

      <Divider sx={{ my: 3 }} />
      <Typography variant="h6">{t('pit_monitor_laps_title')}</Typography>
      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12} md={6}>
          <TextField
            label={t('pit_monitor_laps_times')}
            value={lapTimes}
            onChange={(e) => setLapTimes(e.target.value)}
            multiline
            rows={5}
            fullWidth
            InputProps={{
              startAdornment: (
                <ListItemIcon sx={{ minWidth: 40, mr: 0.5, alignSelf: 'flex-start', mt: 1.5 }}>
                  <TimerIcon />
                </ListItemIcon>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label={t('pit_monitor_laps_notes')}
            value={lapNotes}
            onChange={(e) => setLapNotes(e.target.value)}
            multiline
            rows={5}
            fullWidth
            InputProps={{
              startAdornment: (
                <ListItemIcon sx={{ minWidth: 40, mr: 0.5, alignSelf: 'flex-start', mt: 1.5 }}>
                  <NotesIcon />
                </ListItemIcon>
              ),
            }}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};
// --- FINE PITLANE MONITOR ---


// --- 7. IL NUOVO COMPONENTE "CONTENITORE" ---
function SetupDetail({ teamId, userRole }) {
  const { t, i18n } = useTranslation('setup');
  const { isTrial } = useContext(TeamPlanContext);
  const currentLang = i18n.language;

  // ── Tire lifing post-sessione: dialog + caricamento gomme collegate ──
  const [lifingDialogOpen, setLifingDialogOpen] = useState(false);
  const [lifingTires, setLifingTires] = useState([]);

  const { vehicleId, setupId } = useParams();
  const navigate = useNavigate();

  // Stati
  const [setup, setSetup] = useState(null);
  const [isLoadingSetup, setIsLoadingSetup] = useState(true);
  const [vehicle, setVehicle] = useState(null);
  const [setupTemplate, setSetupTemplate] = useState(null);
  const [teamData, setTeamData] = useState(null); // Stato per i dati del team (nome, logo)
  const [isLoadingVehicleAndTemplate, setIsLoadingVehicleAndTemplate] = useState(true);
  const [checklistTemplates, setChecklistTemplates] = useState([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(true);
  const [attachedChecklist, setAttachedChecklist] = useState(null);
  const [isLoadingChecklist, setIsLoadingChecklist] = useState(true);
  const [checklistDocId, setChecklistDocId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [isAttaching, setIsAttaching] = useState(false);
  const [driverFeedbacks, setDriverFeedbacks] = useState([]);
  const isManager = userRole === 'admin' || userRole === 'Engineer';
  const isMeccanico = userRole === 'Mechanic';

  const getLocaleFormat = () => (currentLang === 'it' ? 'it-IT' : 'en-GB');

  // --- useEffects (Copiati dal vecchio SetupDetail.js) ---
  useEffect(() => {
    setIsLoadingSetup(true);
    const ref = doc(db, 'setupSheets', setupId);

    const unsub = onSnapshot(ref, (snap) => {
      if (snap.exists() && snap.data().teamId === teamId) {
        setSetup(snap.data());
      } else {
        console.error("SetupDetail: Setup not found or access denied");
        setSetup(null);
      }
      setIsLoadingSetup(false);
    }, (err) => {
      console.error("Error loading setup:", err);
      alert(t('detail_loading_error'));
      setIsLoadingSetup(false);
    });

    return () => unsub();
  }, [setupId, teamId, t]);

  useEffect(() => {
    if (!vehicleId || !teamId) return;
    setIsLoadingVehicleAndTemplate(true);
    getDoc(doc(db, 'vehicles', vehicleId))
      .then(vehicleSnap => {
        if (vehicleSnap.exists() && vehicleSnap.data().teamId === teamId) {
          const vehicleData = vehicleSnap.data();
          setVehicle(vehicleData);
          if (vehicleData.templateId) {
            getDoc(doc(db, 'setupTemplates', vehicleData.templateId))
              .then(templateSnap => {
                if (templateSnap.exists()) {
                  setSetupTemplate(templateSnap.data());
                } else { setSetupTemplate(null); }
                setIsLoadingVehicleAndTemplate(false);
              });
          } else { setIsLoadingVehicleAndTemplate(false); }
        } else { setIsLoadingVehicleAndTemplate(false); }
      })
      .catch(err => {
        console.error("Error loading vehicle:", err);
        setIsLoadingVehicleAndTemplate(false);
      });
  }, [vehicleId, teamId]);

  // Fetch Team Data per Export
  useEffect(() => {
    if (!teamId) return;
    getDoc(doc(db, 'teams', teamId)).then(snap => {
      if (snap.exists()) {
        setTeamData(snap.data());
      }
    }).catch(err => console.error("Error loading team:", err));
  }, [teamId]);

  useEffect(() => { if (!teamId) return; setIsLoadingTemplates(true); const q = query(collection(db, "checklistTemplates"), where("teamId", "==", teamId), orderBy("name", "asc")); getDocs(q).then((qs) => { const list = qs.docs.map(d => ({ id: d.id, ...d.data() })); setChecklistTemplates(list); }).catch(err => console.error("Error loading templates:", err)).finally(() => setIsLoadingTemplates(false)); }, [teamId]);

  useEffect(() => { if (!setupId) return; setIsLoadingChecklist(true); const q = query(collection(db, "setupChecklists"), where("setupId", "==", setupId)); const unsub = onSnapshot(q, (qs) => { if (!qs.empty) { const doc = qs.docs[0]; setAttachedChecklist(doc.data()); setChecklistDocId(doc.id); } else { setAttachedChecklist(null); setChecklistDocId(null); } setIsLoadingChecklist(false); }, (err) => { console.error("Error loading specific checklist:", err); setIsLoadingChecklist(false); }); return () => unsub(); }, [setupId, teamId]);

  // Carica i feedback Pilota per questo Setup
  useEffect(() => {
    if (!setupId) return;
    const q = query(collection(db, "driverFeedback"), where("setupId", "==", setupId));
    const unsub = onSnapshot(q, (qs) => {
      const fbs = qs.docs.map(d => ({ id: d.id, ...d.data() }));
      fbs.sort((a, b) => b.createdAt?.toMillis() - a.createdAt?.toMillis());
      setDriverFeedbacks(fbs);
      
      // Marca come letti
      if (isManager) {
        fbs.forEach(fb => {
          if (fb.isUnread) {
            updateDoc(doc(db, "driverFeedback", fb.id), { isUnread: false }).catch(err => console.error("Error updating unread status", err));
          }
        });
      }
    });
    return () => unsub();
  }, [setupId, isManager]);

  // --- FINE useEffects ---

  // --- Funzioni Handler (Copiate dal vecchio SetupDetail.js) ---
  const handleDelete = async () => {
    if (window.confirm(t('detail_delete_confirm'))) {
      try {
        await deleteDoc(doc(db, 'setupSheets', setupId));
        navigate(`/vehicle/${vehicleId}`);
      } catch (err) {
        console.error("Error deleting:", err);
        alert(t('detail_delete_error'));
      }
    }
  };
  const handleDeleteDriverFeedback = async (feedbackId) => {
    if (window.confirm(t('driver_feedback_delete_confirm', { defaultValue: 'Eliminare questa nota del pilota?' }))) {
      try {
        await deleteDoc(doc(db, 'driverFeedback', feedbackId));
      } catch (err) {
        console.error("Error deleting driver feedback:", err);
        alert(t('driver_feedback_delete_error', { defaultValue: 'Errore durante l\'eliminazione.' }));
      }
    }
  };

  const handleOpenDialog = () => { setSelectedTemplateId(''); setDialogOpen(true); };
  const handleCloseDialog = () => { setDialogOpen(false); };

  const handleAttachChecklist = async () => {
    if (!selectedTemplateId) return;
    const template = checklistTemplates.find(t => t.id === selectedTemplateId);
    if (!template) return;

    setIsAttaching(true);
    try {
      await addDoc(collection(db, "setupChecklists"), {
        teamId: teamId,
        vehicleId: vehicleId,
        setupId: setupId,
        templateName: template.name,
        items: template.items.map(txt => ({ text: txt, checked: false })),
        createdAt: serverTimestamp()
      });
      handleCloseDialog();
    } catch (err) {
      console.error("Error attaching checklist:", err);
      alert(t('detail_checklist_attach_error'));
    } finally {
      setIsAttaching(false);
    }
  };

  const handleToggleChecklistItem = async (idx) => {
    if (!attachedChecklist || !checklistDocId) return;
    const updatedItems = attachedChecklist.items.map((item, i) => (i === idx ? { ...item, checked: !item.checked } : item));
    const ref = doc(db, "setupChecklists", checklistDocId);
    try {
      await updateDoc(ref, { items: updatedItems });
    } catch (err) {
      console.error("Error updating item:", err);
      alert(t('detail_checklist_update_error'));
    }
  };

  const handleDetachChecklist = async () => {
    if (!checklistDocId) return;
    if (window.confirm(t('detail_checklist_detach_confirm'))) {
      try {
        await deleteDoc(doc(db, "setupChecklists", checklistDocId));
        console.log("Checklist detached.");
      } catch (err) {
        console.error("Error detaching:", err);
        alert(t('detail_checklist_detach_error'));
      }
    }
  };

  const handleExportExcel = () => {
    if (!setup || !vehicle) return;
    exportSetupToExcel(
      setupData,
      setup.eventName || 'Setup',
      vehicle.name || 'Vehicle',
      setup.trackName || 'Track',
      displayDate,
      teamData?.name || 'My Team',
      t
    );
  };

  const handleExportPdf = async () => {
    if (!setup || !vehicle) return;
    try {
      let proxyLogoUrl = null;
      if (teamData?.logoUrlLight) {
          proxyLogoUrl = `https://wsrv.nl/?url=${encodeURIComponent(teamData.logoUrlLight)}&output=png`;
      }
      
      let proxyVehicleUrl = null;
      if (vehicle?.imageUrl) {
          proxyVehicleUrl = `https://wsrv.nl/?url=${encodeURIComponent(vehicle.imageUrl)}&output=png`;
      }

      const blob = await pdf(
        <SetupPdf 
          setupData={setupData}
          setup={setup}
          vehicleData={vehicle} 
          teamData={teamData} 
          teamLogoBase64={proxyLogoUrl} 
          vehicleImageBase64={proxyVehicleUrl}
          templateConfig={setupTemplate?.modules || []}
          t={t}
          language={i18n.language?.startsWith('en') ? 'en' : 'it'}
          displayDate={displayDate}
          driverFeedbacks={driverFeedbacks}
          showWatermark={isTrial}
        />
      ).toBlob();
      
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${vehicle?.name || vehicle?.modello || 'Vehicle'}_Setup_${setup.eventName || 'Event'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Errore durante la generazione del PDF. Riprova.");
    }
  };
  // --- FINE Funzioni Handler ---

  // --- 8. LOGICA DI RENDER DINAMICO ---

  // Unisce i dati salvati con lo stato iniziale completo per evitare errori
  const setupData = useMemo(() => {
    if (!setup) return newInitialSetupState;

    const mergedData = { ...newInitialSetupState };
    for (const key of Object.keys(newInitialSetupState)) {
      if (setup.setupData && setup.setupData[key]) {
        mergedData[key] = { ...newInitialSetupState[key], ...setup.setupData[key] };
      }
    }
    return mergedData;
  }, [setup]);

  // Calcoli (es. zavorra)
  const { eventName, sessionName, nomePilota, dataSessione } = setup || {};
  const displayDate = dataSessione ? new Date(dataSessione.toDate()).toLocaleDateString(getLocaleFormat()) : 'N/A';
  const dryWeight = Number(setupData.weights?.dryWeight || 0);
  const driverWeight = Number(setupData.weights?.driverWeight || 0);
  const fuelLiters = Number(setupData.weights?.fuelLiters || 0);
  const minWeight = Number(setupData.weights?.minRegWeight || 0);
  const fuelWeight = fuelLiters * 0.75;
  const totalCalculatedWeight = dryWeight + driverWeight + fuelWeight;
  const calculatedBallastValue = minWeight > 0 ? (minWeight - totalCalculatedWeight).toFixed(1) : 'N/A';



  // --- RENDER ---

  const isPageLoading = isLoadingSetup || isLoadingTemplates || isLoadingChecklist || isLoadingVehicleAndTemplate;
  if (isPageLoading) {
    return (<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}><CircularProgress /></Box>);
  }

  if (!setup) {
    return (
      <Box sx={{ textAlign: 'center', mt: 5 }}>
        <Typography variant="h4" color="error">{t('detail_not_found')}</Typography>
        <Button component={RouterLink} to={`/vehicle/${vehicleId}`} sx={{ mt: 2 }}>
          {t('detail_back_button')}
        </Button>
      </Box>
    );
  }

  if (!setupTemplate) {
    return (
      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Typography variant="h5" color="error">{t('error_no_template_title')}</Typography>
        <Typography sx={{ mt: 2 }}>{t('error_no_template_subtitle')}</Typography>
        <Button
          variant="contained"
          component={RouterLink}
          to={`/vehicle/${vehicleId}`}
          sx={{ mt: 2 }}
        >
          {t('error_no_template_button')}
        </Button>
      </Box>
    );
  }

  const paperStyle = {
    p: { xs: 2, sm: 3 },
    mb: 3,
    border: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'background.paper'
  };


  return (
    <>
      <Box className="no-print">
        {/* 1. Header */}
        <Paper
          elevation={0}
          sx={{ p: 3, mb: 3, backgroundColor: 'background.paper', border: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton color="inherit" component={RouterLink} to={`/vehicle/${vehicleId}`} sx={{ mr: 2, p: 0.5 }}>
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
                {eventName || 'Event'} - {sessionName || 'Session'}
              </Typography>
              <Typography variant="h6" color="text.secondary">
                {displayDate} ({vehicle?.templateName || 'No Template'})
              </Typography>
            </Box>
          </Box>
          <Box>
            {isManager && (
              <>
                <Tooltip title={t('detail_edit_tooltip')}>
                  <IconButton color="inherit" component={RouterLink} to={`/vehicle/${vehicleId}/setup/${setupId}/edit`} size="large"><EditIcon /></IconButton>
                </Tooltip>
                <Tooltip title={t('detail_delete_tooltip')}>
                  <IconButton onClick={handleDelete} color="error" size="large"><DeleteIcon /></IconButton>
                </Tooltip>
              </>
            )}
            <Tooltip title={t('detail_export_pdf', { defaultValue: 'Esporta PDF' })}>
              <IconButton onClick={handleExportPdf} color="primary" size="large" sx={{ ml: 1 }}>
                <PictureAsPdfIcon />
              </IconButton>
            </Tooltip>
            {/* Bottone "Aggiorna usura": visibile solo se almeno una gomma è collegata al DB */}
            {(userRole === 'admin' || userRole === 'Engineer' || userRole === 'Mechanic') &&
              ['gomme_ant_sx', 'gomme_ant_dx', 'gomme_post_sx', 'gomme_post_dx'].some(k => setupData?.[k]?.tireId) && (
              <Tooltip title={t('tires:lifing_title_bulk')}>
                <IconButton onClick={async () => {
                  const ids = ['gomme_ant_sx', 'gomme_ant_dx', 'gomme_post_sx', 'gomme_post_dx']
                    .map(k => setupData?.[k]?.tireId)
                    .filter(Boolean);
                  const results = await Promise.all(ids.map(id => getTire(id).catch(() => null)));
                  setLifingTires(results.filter(Boolean));
                  setLifingDialogOpen(true);
                }} color="secondary" size="large" sx={{ ml: 1 }}>
                  <TimelineIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Paper>

        {/* 2. Pit Lane Monitor (Invariato) */}
        {
          (isManager || isMeccanico) && (
            <PitLaneMonitor
              setupData={setupData}
              setupId={setupId}
              isManager={isManager}
              isMeccanico={isMeccanico}
              t={t}
            />
          )
        }

        {/* 3. Info Sessione (Invariato) */}
        <Paper elevation={0} sx={paperStyle}>
          <Typography variant="h5" gutterBottom>{t('detail_session_title')}</Typography>
          <Grid container spacing={2}>
            <DataDisplay label={t('detail_session_event')} value={eventName} />
            <DataDisplay label={t('detail_session_session')} value={sessionName} />
            <DataDisplay label={t('detail_session_date')} value={displayDate} />
            <DataDisplay label={t('detail_session_driver')} value={nomePilota} />
            <DataDisplay label={t('field_air_temp')} value={setupData.meteo?.tempAria} unit="°C" />
            <DataDisplay label={t('field_track_temp')} value={setupData.meteo?.tempAsfalto} unit="°C" />
            <DataDisplay label={t('field_humidity')} value={setupData.meteo?.umidita} unit="%" />
          </Grid>
        </Paper>

        {/* 4. Sezione Checklist (Invariata) */}
        <Paper elevation={0} sx={paperStyle}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              {t('detail_checklist_title')}
              {attachedChecklist && (
                <Tooltip title={t('detail_checklist_detach_tooltip')}>
                  <IconButton onClick={handleDetachChecklist} size="small" sx={{ ml: 1 }}>
                    <LinkOffIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Typography>
            {!attachedChecklist && (
              <Button variant="outlined" startIcon={<PlaylistAddCheckIcon />} onClick={handleOpenDialog} disabled={isLoadingTemplates || checklistTemplates.length === 0}>
                {checklistTemplates.length === 0 ? t('detail_checklist_no_templates') : t('detail_checklist_attach_button')}
              </Button>
            )}
          </Box>
          {attachedChecklist ? (
            <>
              <Typography variant="subtitle1" gutterBottom>{t('detail_checklist_template_prefix')} {attachedChecklist.templateName}</Typography>
              <List dense>
                {attachedChecklist.items.map((item, index) => (
                  <ListItem key={index} disablePadding>
                    <ListItemIcon sx={{ minWidth: 0, mr: 1 }}>
                      <Checkbox edge="start" checked={item.checked} onChange={() => handleToggleChecklistItem(index)} tabIndex={-1} disableRipple />
                    </ListItemIcon>
                    <ListItemText primary={item.text} sx={{ textDecoration: item.checked ? 'line-through' : 'none', color: item.checked ? 'text.secondary' : 'text.primary' }} />
                  </ListItem>
                ))}
              </List>
            </>
          ) : (<Typography variant="body2" sx={{ fontStyle: 'italic' }}>{t('detail_checklist_not_attached')}</Typography>)}
        </Paper>

        {/* --- VISTA CAR-CENTRIC --- */}
        <CarCentricView
          setupData={setupData}
          setupTemplate={setupTemplate}
          totalCalculatedWeight={totalCalculatedWeight}
          calculatedBallastValue={calculatedBallastValue}
          t={t}
        />
        {/* --- FINE VISTA CAR-CENTRIC --- */}

        {/* --- RENDER DRIVER FEEDBACKS --- */}
        {driverFeedbacks.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', color: 'secondary.main' }}>
              <CampaignIcon sx={{ mr: 1 }} /> {t('driver_feedback_title')}
            </Typography>
            {driverFeedbacks.map(fb => (
              <Paper key={fb.id} sx={{ p: 2, mb: 2, border: '2px solid', borderColor: 'secondary.main', backgroundColor: 'background.default' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">{fb.driverName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {fb.createdAt?.toDate().toLocaleString(getLocaleFormat())}
                    </Typography>
                  </Box>
                  {isManager && (
                    <Tooltip title={t('driver_feedback_delete_tooltip', { defaultValue: 'Elimina nota' })}>
                      <IconButton size="small" color="error" onClick={() => handleDeleteDriverFeedback(fb.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                {fb.corners && fb.corners.map((c, i) => (
                  <Box key={i} sx={{ mb: 2, pl: 2, borderLeft: '3px solid', borderColor: 'secondary.main' }}>
                    <Typography variant="body2" fontWeight="bold">{c.name}</Typography>
                    <Typography variant="body2">{t('driver_feedback_entry')}: {c.entry}/10 | {t('driver_feedback_mid')}: {c.mid}/10 | {t('driver_feedback_exit')}: {c.exit}/10</Typography>
                    {c.notes && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                        <MicIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />"{c.notes}"
                      </Typography>
                    )}
                  </Box>
                ))}
              </Paper>
            ))}
          </Box>
        )}
        {/* --- FINE DRIVER FEEDBACKS --- */}

        {/* --- 8. DIALOG CHECKLIST --- */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog}>
          <DialogTitle>{t('detail_checklist_modal_title')}</DialogTitle>
          <DialogContent>
            <Typography variant="body1" gutterBottom>{t('detail_checklist_modal_subtitle')}</Typography>
            {isLoadingTemplates ? <CircularProgress /> : (
              <RadioGroup value={selectedTemplateId} onChange={(e) => setSelectedTemplateId(e.target.value)}>
                {checklistTemplates.map((t) => (
                  <FormControlLabel key={t.id} value={t.id} control={<Radio />} label={t.name} />
                ))}
              </RadioGroup>
            )}
            {checklistTemplates.length === 0 && !isLoadingTemplates && (
              <Alert severity="warning">{t('detail_checklist_no_templates_alert')}</Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>{t('modal_general_cancel')}</Button>
            <Button onClick={handleAttachChecklist} disabled={!selectedTemplateId || isAttaching} variant="contained">
              {isAttaching ? <CircularProgress size={24} /> : t('detail_checklist_modal_attach')}
            </Button>
          </DialogActions>
        </Dialog>
        {/* --- FINE DIALOG --- */}

        {/* Dialog aggiornamento usura gomme post-sessione */}
        <TireLifingForm
          open={lifingDialogOpen}
          onClose={() => setLifingDialogOpen(false)}
          onSaved={() => {/* CornerCard ricaricherà al prossimo render */}}
          tires={lifingTires}
          title={t('tires:lifing_title_bulk')}
        />
      </Box>

      {/* Box 2: Layout di Stampa (Invariato) */}
      <Box className="print-only">
        <SetupPrintLayout setup={setup} />
      </Box>
    </>
  );
}

export default SetupDetail;